import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";

interface ComposerProps {
  disabled: boolean;
  isStreaming: boolean;
  onSend: (text: string) => Promise<void>;
  onStop: () => void;
}

export function Composer({ disabled, isStreaming, onSend, onStop }: ComposerProps) {
  const [text, setText] = useState("");
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  // Track IME composition state so Enter pressed mid-composition (e.g. to
  // pick a Chinese candidate) does NOT submit the form.
  const isComposingRef = useRef(false);

  // Auto-grow textarea up to its max-height so the composer doesn't look
  // single-line when the user pastes a long block.
  useEffect(() => {
    const node = textAreaRef.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, 200)}px`;
  }, [text]);

  async function submit() {
    const prompt = text.trim();
    if (!prompt || disabled) {
      return;
    }
    setText("");
    try {
      await onSend(prompt);
    } finally {
      // Always re-focus, even if onSend threw, so the user can immediately
      // retry without clicking back into the textarea.
      textAreaRef.current?.focus();
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submit();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== "Enter" || event.shiftKey) return;
    // Safari/Chrome both report keyCode 229 while an IME composition is
    // active. We also keep our own ref as a belt-and-braces guard because
    // some IMEs fire the keydown *just after* compositionend with key=Enter
    // but with the composition still semantically open (e.g. selecting a
    // candidate); when that happens nativeEvent.isComposing is still true.
    const nativeEvt = event.nativeEvent as KeyboardEvent["nativeEvent"] & {
      isComposing?: boolean;
      keyCode?: number;
    };
    if (isComposingRef.current || nativeEvt.isComposing || nativeEvt.keyCode === 229) {
      return;
    }
    if (isTouchLikely()) return;
    event.preventDefault();
    void submit();
  }

  return (
    <form className="composer" onSubmit={handleSubmit}>
      <textarea
        ref={textAreaRef}
        value={text}
        rows={1}
        placeholder={disabled ? "先选择或新建一个会话" : "询问车型、改装方案、调校取舍..."}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => {
          isComposingRef.current = true;
        }}
        onCompositionEnd={() => {
          // Defer one frame: some browsers (Chrome on macOS especially) fire
          // the Enter keydown *after* compositionend in the same tick when the
          // user picks a candidate with Enter. Releasing the flag right away
          // would let that Enter submit the form.
          setTimeout(() => {
            isComposingRef.current = false;
          }, 0);
        }}
        disabled={disabled}
      />
      {isStreaming ? (
        <button type="button" className="secondary-button" onClick={onStop}>
          停止
        </button>
      ) : (
        <button type="submit" className="primary-button compact" disabled={disabled || !text.trim()}>
          发送
        </button>
      )}
    </form>
  );
}

function isTouchLikely(): boolean {
  // navigator.userAgentData has touch info on Chromium; fall back to the
  // legacy touch checks. We treat anything not obviously a desktop pointer
  // as "touch likely" so Enter doesn't accidentally submit on phones where
  // users press Enter for newline.
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(pointer: coarse)").matches) return true;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}
